import { useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Upload, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, errMsg, useRefresh } from "@/lib/api";

const FIELDS = [
  { id: "first_name", label: "Nome", keys: ["nome", "first name", "firstname", "name"] },
  { id: "last_name", label: "Cognome", keys: ["cognome", "last name", "lastname", "surname"] },
  { id: "phone", label: "Telefono", keys: ["telefono", "cellulare", "tel", "phone", "mobile", "numero"] },
  { id: "email", label: "Email", keys: ["email", "e-mail", "mail", "posta"] },
  { id: "city", label: "Città", keys: ["città", "citta", "comune", "city", "località"] },
  { id: "birth_date", label: "Data di nascita", keys: ["nascita", "data di nascita", "birth", "compleanno"] },
  { id: "tax_code", label: "Codice fiscale", keys: ["codice fiscale", "cf", "c.f.", "fiscale"] },
  { id: "job", label: "Professione", keys: ["professione", "lavoro", "occupazione", "job"] },
  { id: "income", label: "Reddito", keys: ["reddito", "stipendio", "income"] },
  { id: "interests", label: "Prodotto di interesse", keys: ["prodotto", "interesse", "richiesta", "product"] },
  { id: "source", label: "Fonte", keys: ["fonte", "provenienza", "source"] },
  { id: "notes", label: "Note", keys: ["note", "notes", "commento"] },
];

const guess = (h) => {
  const n = String(h).toLowerCase().trim();
  return FIELDS.find((f) => f.keys.some((k) => n === k))?.id || FIELDS.find((f) => f.keys.some((k) => n.includes(k)))?.id || "";
};

export const ImportDialog = ({ open, onOpenChange }) => {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [map, setMap] = useState({});
  const [file, setFile] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = useRefresh();

  const reset = () => { setRows([]); setHeaders([]); setMap({}); setFile(""); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const wb = XLSX.read(await f.arrayBuffer(), { cellDates: true });
      const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "", raw: false, dateNF: "yyyy-mm-dd" });
      if (!data.length) return toast.error("Il file è vuoto");
      const hs = Object.keys(data[0]);
      setHeaders(hs);
      setMap(Object.fromEntries(hs.map((h) => [h, guess(h)])));
      setRows(data.slice(0, 2000));
      setFile(f.name);
    } catch { toast.error("Impossibile leggere il file. Usa un file Excel (.xlsx, .xls) o CSV."); }
  };

  const submit = async () => {
    const mapped = rows.map((r) => {
      const o = {};
      headers.forEach((h) => { const k = map[h]; if (k) o[k] = o[k] ? `${o[k]} ${r[h]}` : r[h]; });
      if (!o.last_name && o.first_name && map && !Object.values(map).includes("last_name")) {
        const parts = String(o.first_name).trim().split(/\s+/);
        if (parts.length > 1) { o.first_name = parts.shift(); o.last_name = parts.join(" "); }
      }
      return o;
    });
    if (!Object.values(map).some((v) => v === "first_name" || v === "last_name")) return toast.error("Associa almeno la colonna Nome o Cognome");
    setBusy(true);
    try {
      const { data } = await api.post("/clients/import", { rows: mapped });
      toast.success(`Importati ${data.created} nominativi${data.skipped ? `, ${data.skipped} già presenti` : ""}${data.invalid ? `, ${data.invalid} righe senza nome` : ""}`);
      refresh();
      reset();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle className="font-display">Importa nominativi da Excel o CSV</DialogTitle></DialogHeader>
        {!rows.length ? (
          <label data-testid="import-dropzone" className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-300 py-14 cursor-pointer hover:border-sky-400 hover:bg-sky-50/40 transition-colors">
            <Upload className="h-8 w-8 text-sky-600" />
            <p className="font-medium">Scegli un file .xlsx, .xls o .csv</p>
            <p className="text-xs text-slate-500 max-w-sm text-center">La prima riga deve contenere i titoli delle colonne (es. Nome, Cognome, Telefono, Email). I nominativi già presenti (stesso telefono o email) vengono saltati.</p>
            <input data-testid="import-file-input" type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} />
          </label>
        ) : (
          <div className="space-y-5">
            <p className="text-sm flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /><b>{file}</b> · <span data-testid="import-row-count">{rows.length}</span> righe trovate</p>
            <div>
              <p className="text-xs text-slate-500 mb-2">Controlla a quale campo corrisponde ogni colonna:</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {headers.map((h, i) => (
                  <div key={h} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                    <span className="text-sm font-medium truncate flex-1" title={h}>{h}</span>
                    <Select value={map[h] || "none"} onValueChange={(v) => setMap({ ...map, [h]: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid={`import-map-${i}`} className="w-44 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Ignora —</SelectItem>
                        {FIELDS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="text-xs w-full" data-testid="import-preview">
                <thead className="bg-slate-50"><tr>{headers.map((h) => <th key={h} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
                <tbody>{rows.slice(0, 5).map((r, i) => <tr key={i} className="border-t">{headers.map((h) => <td key={h} className="px-3 py-1.5 whitespace-nowrap">{String(r[h])}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter>
          {rows.length > 0 && <Button variant="outline" onClick={reset}>Cambia file</Button>}
          <Button data-testid="import-submit-btn" disabled={!rows.length || busy} onClick={submit} className="bg-[#0F172A] hover:bg-sky-600">{busy ? "Importazione…" : `Importa ${rows.length || ""} nominativi`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
